import api, { apiInstance } from '@/api/client'
import { extractApiErrorCode } from '@/utils/api/mapApiError'
import {
  getAuthToken,
  getRefreshToken,
  isTokenExpired,
  removeAuthTokens,
  setAuthToken,
  setRefreshToken
} from '@/utils/auth'

/** Claims of shared/jwt `Claims`; the account id is `user_id`. */
export interface JwtAccess {
  user_id: string
  roles?: string[]
  exp: number
}

export interface JwtRefresh {
  user_id: string
  exp: number
}

export async function clearTokens() {
  removeAuthTokens()
  apiInstance.nextAuthAccessToken = ''
}

// AU-204: the refresh token is revoked — logged out, or replayed after the
// one-minute grace window in which the winner of a concurrent refresh
// republishes its pair. Either way it will never be accepted again.
const CodeTokenRevoked = 'AU-204'

/**
 * api-gateway serialises every business error as HTTP 400 (shared/error's
 * FiberErrToJSON), so the status alone cannot tell a dead session from a
 * transient failure — a revoked token arrives as 400, lands in the "keep the
 * session" branch, and leaves the app holding cookies the server will never
 * accept: no Authorization header, 401 on every call, and a store still
 * claiming the user is logged in (BAT-850 follow-up). Read the error code too.
 */
const isRejectedByServer = (error: unknown): boolean => {
  if (extractApiErrorCode(error) === CodeTokenRevoked) {
    return true
  }
  const status = (error as { status?: unknown } | null)?.status
  return status === 401 || status === 403
}

// Concurrent-refresh grace window. When two tabs refresh at once the refresh
// token is spent by the winner, and the loser gets AU-204. The winner publishes
// the new pair to the shared cookies, but that can land *after* the loser's
// error — especially when user-profiles/agents/clubs are slow, which widens the
// window (OCE race condition). A single snapshot of the cookie at error time is
// therefore not enough: poll a few times with a small backoff so the loser can
// adopt the winner's fresh pair instead of force-logging-out on a token that was
// never actually revoked.
const REFRESH_ADOPT_ATTEMPTS = 6
const REFRESH_ADOPT_DELAY_MS = 100

const delay = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms))

type AdoptResult = { changed: boolean; access?: string }

/**
 * Wait (briefly, with backoff) for another tab / the SSR middleware to publish a
 * fresh pair after our own refresh lost the race. Returns as soon as the stored
 * refresh token differs from the one we tried to spend. `access` is set only
 * when a still-valid access token is available to reuse immediately; the session
 * is considered alive either way, so the caller must not clear tokens.
 */
async function waitForRefreshElsewhere(
  spentRefresh: string
): Promise<AdoptResult> {
  for (let attempt = 0; attempt < REFRESH_ADOPT_ATTEMPTS; attempt++) {
    const currentRefresh = getRefreshToken()
    if (currentRefresh && currentRefresh !== spentRefresh) {
      const access = getAuthToken()
      if (access && !isTokenExpired(access)) {
        apiInstance.nextAuthAccessToken = access
        return { changed: true, access }
      }
      return { changed: true }
    }
    await delay(REFRESH_ADOPT_DELAY_MS)
  }
  return { changed: false }
}

/**
 * A failed refresh is only a lost session when the *server* rejected the token
 * and nobody else has refreshed meanwhile. Two other actors share these cookies
 * — another tab and the SSR middleware (`lib/auth/refreshSessionCookies.ts`) —
 * and a refresh token can only be spent once, so a losing caller must adopt the
 * winner's cookies instead of wiping them. Transient failures (5xx, dropped
 * connection) keep the session too: the next request retries.
 */
async function performRefresh(): Promise<string | undefined> {
  const refreshToken = getRefreshToken()

  if (refreshToken) {
    try {
      const { access_token, refresh_token } = await api.v2AuthRefreshCreate({
        refresh_token: refreshToken
      })

      if (access_token && refresh_token) {
        setAuthToken(access_token)
        setRefreshToken(refresh_token)

        apiInstance.nextAuthAccessToken = access_token

        return access_token
      }
    } catch (error) {
      const currentAccess = getAuthToken()
      if (currentAccess && !isTokenExpired(currentAccess)) {
        apiInstance.nextAuthAccessToken = currentAccess

        return currentAccess
      }

      const currentRefresh = getRefreshToken()
      const refreshedElsewhere =
        Boolean(currentRefresh) && currentRefresh !== refreshToken

      // Already adopted (the winner beat us to the cookie), or a transient
      // failure — keep the session, the next request retries.
      if (refreshedElsewhere || !isRejectedByServer(error)) {
        return
      }

      // Server rejected the token and nobody has refreshed *yet*. This is the
      // ambiguous case: a genuine revoke looks identical to losing a concurrent
      // refresh whose winner has not published the new pair. Give the winner a
      // short grace window before deciding it was a real revoke (OCE).
      const adopted = await waitForRefreshElsewhere(refreshToken)
      if (adopted.changed) {
        return adopted.access
      }

      await clearTokens()

      return
    }
  }
}

// Cross-tab single-flight. Web Locks is shared across all same-origin tabs, so
// only the lock holder performs the network refresh; the others queue on the
// same lock and, once it is released, reuse the pair the winner just published
// instead of spending the (now consumed) refresh token themselves. This removes
// the concurrent-refresh race at the source; waitForRefreshElsewhere above stays
// as a safety net for environments without Web Locks and for the SSR middleware.
const AUTH_REFRESH_LOCK = 'auth-refresh'

function webLocksAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.locks?.request === 'function'
  )
}

export async function refreshTokens(): Promise<string | undefined> {
  if (!webLocksAvailable()) {
    return performRefresh()
  }

  const spentRefresh = getRefreshToken()

  return navigator.locks.request(AUTH_REFRESH_LOCK, async () => {
    // Another tab may have refreshed while we waited for the lock. If a valid
    // access token is already available and the refresh cookie moved on, adopt
    // it without spending our (stale) refresh token on the network.
    const access = getAuthToken()
    const currentRefresh = getRefreshToken()
    const refreshedWhileWaiting =
      Boolean(spentRefresh) &&
      Boolean(currentRefresh) &&
      currentRefresh !== spentRefresh

    if (refreshedWhileWaiting && access && !isTokenExpired(access)) {
      apiInstance.nextAuthAccessToken = access
      return access
    }

    return performRefresh()
  })
}
